import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import Callout from "components/callout/callout"
import FormErrors from "components/formParts/formErrors"
import { useTranslation } from "react-i18next"
import TextInput from "components/inputs/textInput"
import ToggleInput from "components/inputs/toggleInput"
import Button from "components/inputs/button"
import ButtonGroup from "components/buttons/buttonGroup"
import ProcessingButton from "components/inputs/processingButton"
import useStorages from "hooks/useStorages"
import useLayout from "hooks/useLayout"
import Form from "components/forms/form"
import { ReactComponent as DeleteIcon } from "assets/icons/delete.svg"
import Section from "components/layout/section"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import { logger } from "utils/secureLogger"
import { clearTargetStorage, getTargetStorageDocId } from "@/utils/targetStorage"

interface DeleteStorageFormProps {
    storageId: string
}

const DeleteStorageForm: React.FC<DeleteStorageFormProps> = ({ storageId }) => {

    const { t } = useTranslation()
    const navigate = useNavigate()
    const { addToast } = useLayout()

    // Form data.
    const [name, setName] = useState(``)
    const [confirmation, setConfirmation] = useState(``)
    const [confirmed, setConfirmed] = useState(false)
    const [instantDeletion, setInstantDeletion] = useState(false)

    // Form status.
    const [errors, setErrors] = useState<string[]>([]);

    const {
        storage,
        deleteStorageAsync,
        isDeleting
    } = useStorages({storageId})

    // Set data from storage.
    useEffect(() => {
        if (storage) {
            setName(storage.name);
        }
    }, [storage])

    // Handle form submit.
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!confirmed) return
        if (storageId === undefined) return
        
        try {
            await deleteStorageAsync({ storageId, instantDeletion })
            
            logger.info('Storage deletion successful', { storageId }, 'DeleteStorage')
            addToast({ text: t('toastStorageDeleted'), colorScheme: 'green' })
            if (getTargetStorageDocId() === storageId) clearTargetStorage()
            navigate('/storages')
        } catch (error: unknown) {
            logger.error('Failed to delete storage', error)
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error))
            setErrors([errorMessage])
            addToast({ text: t('toastStorageError'), colorScheme: 'red' })
        }
    }

    // Handle confirmation change.
    const handleConfirmationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmation(e.target.value)
        setConfirmed(e.target.value === name)
    }

    // Catch missing ID or failed loading storage.
    if (storageId === undefined) return <Callout type={'error'}>{t('storageIdMissing')}</Callout>

    // Print the form.
    return (
        <Form
            onSubmit={handleSubmit}
            className={`group`}
            isLoading={isDeleting}
        >
            <Section>
                <p>{t('deleteStorageConfirmation', { name })}</p>
                <TextInput label={t('typeStorageNameToConfirm')} placeholder={name} value={confirmation} onChange={(e) => handleConfirmationChange(e)} />
                <ToggleInput
                    label={t('fastDeletion')}
                    checked={instantDeletion}
                    onChange={(e) => setInstantDeletion(e.target.checked)}
                />
                <ButtonGroup>
                    {isDeleting ? (
                        <ProcessingButton design="danger" />
                    ) : (
                        <Button wideOnMobile type="submit" design="danger" disabled={!confirmed}><DeleteIcon /><span>{t('delete')}</span></Button>
                    )}
                </ButtonGroup>
                {errors.length > 0 && <FormErrors errors={errors} />}
            </Section>
        </Form>
    )
}

export default DeleteStorageForm



